import { faEllipsisVertical, faPen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { classNames } from '@framework/utils';
import { useCallback, useEffect, useState } from 'react';
import { ViewModalTagRemoveConfirmModal } from './ViewModelTagRemoveConfirmModal';
import { ViewModelTagRenameModal } from './ViewModelTagRenameModal';
import { useNavigate } from 'react-router';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { useTranslation } from 'react-i18next';

type Props = {
  groupId: string;
  viewModelTagId: string;
  tagName: string;
  setTagName: React.Dispatch<React.SetStateAction<string>>;
};

type ModalTarget = 'Rename' | 'Remove';

export const ViewModelTagEditor: React.FC<Props> = ({ groupId, viewModelTagId, tagName, setTagName }: Props) => {
  const { t } = useTranslation();
  const {
    isOpen: isRemoveButtonPopupOpen,
    popupRef: removeButtonPopupRef,
    handleClose: handleCloseRemoveButtonPopup,
    handleOpen: handleOpenRemoveButtonPopup,
  } = usePopupRef<HTMLDivElement>(false);

  const [modalTarget, setModalTarget] = useState<ModalTarget | null>(null);
  const [isTagRemoved, setTagRemoved] = useState(false);

  const navigate = useNavigate();

  const showRenameModal = useCallback(() => setModalTarget('Rename'), []);
  const showRemoveModal = useCallback(() => setModalTarget('Remove'), []);
  const closeModal = useCallback(() => {
    setModalTarget(null);
    handleCloseRemoveButtonPopup();
  }, [handleCloseRemoveButtonPopup]);

  useEffect(() => {
    if (isTagRemoved) {
      // タグが削除されたらグループトップ画面に遷移
      navigate(UserPagePathBuilder.groupPage(groupId));
    }
  }, [groupId, navigate, isTagRemoved]);

  return (
    <div>
      <div className="inline-flex">
        <button
          className="rounded-l bg-gray-500 py-1 pl-3 pr-2 font-bold text-white hover:bg-gray-700 focus:outline-none"
          onClick={showRenameModal}
        >
          <FontAwesomeIcon icon={faPen} className="mr-1" />
          {t('common.action.edit')}
        </button>
        <button
          className="rounded-r bg-gray-500 px-2 py-1 hover:bg-gray-700 focus:outline-none"
          onClick={handleOpenRemoveButtonPopup}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} className="text-white" />
        </button>
      </div>
      {modalTarget === 'Rename' && (
        <ViewModelTagRenameModal
          isOpen={true}
          onClose={closeModal}
          groupId={groupId}
          viewModelTagId={viewModelTagId}
          tagName={tagName}
          setTagName={setTagName}
        />
      )}

      <div className="relative">
        <div
          className={classNames(
            'absolute right-0 z-10 w-32 rounded-lg bg-white py-1 shadow-lg',
            isRemoveButtonPopupOpen ? 'block' : 'hidden'
          )}
          ref={removeButtonPopupRef}
        >
          <button
            className="block w-full px-2 py-1 text-left text-red-700 hover:bg-indigo-500 hover:text-white"
            onClick={showRemoveModal}
          >
            <span className="inline-block w-6 text-center">
              <FontAwesomeIcon icon={faTrashAlt} />
            </span>
            {t('common.action.delete')}
          </button>
        </div>
      </div>
      {modalTarget === 'Remove' && (
        <ViewModalTagRemoveConfirmModal
          isOpen={true}
          onClose={closeModal}
          groupId={groupId}
          viewModelTagId={viewModelTagId}
          tagName={tagName}
          setTagRemoved={setTagRemoved}
        />
      )}
    </div>
  );
};
